import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { eventAPI } from '../services/api';

function EventDetails() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchEventDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchEventDetails = async () => {
    try {
      setLoading(true);
      const response = await eventAPI.getById(id);
      setEvent(response.data);
      
      // Try to fetch availability separately
      try {
        const availResponse = await eventAPI.getAvailability(id);
        setAvailability(availResponse.data);
      } catch (err) {
        console.log('Availability not available');
      }
    } catch (err) {
      setError('Failed to load event details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Date TBD';
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getAvailabilityStatus = (available, capacity) => {
    const percentage = (available / capacity) * 100;
    if (percentage === 0) return { status: 'Sold Out', color: '#e74c3c' };
    if (percentage < 20) return { status: 'Limited', color: '#f39c12' };
    return { status: `${available} Available`, color: '#27ae60' };
  };

  const tierPrices = {
    silver: 299,
    gold: 499,
    platinum: 799,
    diamond: 1299,
    child: 99
  };

  const tierDescriptions = {
    silver: 'General admission with standard access',
    gold: 'Enhanced experience with premium viewing areas',
    platinum: 'VIP treatment with exclusive amenities',
    diamond: 'Ultimate luxury experience with all perks',
    child: 'Special pricing for children under 12'
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <div className="loading">Loading event details...</div>
    </div>
  );
  
  if (error) return <div className="error-message">{error}</div>;
  if (!event) return <div>Event not found</div>;

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem' }}>
      <Link to="/" style={{ 
        display: 'inline-flex', 
        alignItems: 'center', 
        color: '#3498db', 
        textDecoration: 'none',
        marginBottom: '2rem',
        fontSize: '0.95rem'
      }}>
        ← Back to Events
      </Link>
      
      {/* Event Header */}
      <div style={{ 
        background: 'white', 
        borderRadius: '12px', 
        padding: '2rem',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        marginBottom: '2rem'
      }}>
        <h1 style={{ margin: 0, fontSize: '2.5rem', color: '#2c3e50' }}>{event.name}</h1>
        
        {event.description && (
          <p style={{ 
            marginTop: '1rem', 
            fontSize: '1.1rem',
            color: '#666',
            lineHeight: '1.6'
          }}>
            {event.description}
          </p>
        )}
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '1rem',
          marginTop: '2rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '1.5rem' }}>📅</span>
            <div>
              <div style={{ fontSize: '0.875rem', color: '#999' }}>Date & Time</div>
              <div style={{ fontWeight: '500' }}>
                {event.date || formatDate(event.start_time)}
              </div>
            </div>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '1.5rem' }}>📍</span>
            <div>
              <div style={{ fontSize: '0.875rem', color: '#999' }}>Venue</div>
              <div style={{ fontWeight: '500' }}>
                {event.venue || event.venue_name || event.location || 'TBD'}
              </div>
            </div>
          </div>
          
          {event.artist && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '1.5rem' }}>🎤</span>
              <div>
                <div style={{ fontSize: '0.875rem', color: '#999' }}>Artists</div>
                <div style={{ fontWeight: '500' }}>{event.artist}</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Ticket Availability */}
      {availability && availability.booking_stats && (
        <div>
          <h2 style={{ fontSize: '1.75rem', marginBottom: '1.5rem', color: '#2c3e50' }}>
            Ticket Availability
          </h2>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '1.5rem'
          }}>
            {Object.entries(availability.booking_stats.tier_stats || {}).map(([tier, stats]) => {
              const availStatus = getAvailabilityStatus(stats.available, stats.capacity);
              const soldPercentage = ((stats.capacity - stats.available) / stats.capacity) * 100;
              
              return (
                <div key={tier} style={{ 
                  background: 'white',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                }}>
                  {/* Tier Badge */}
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start',
                    marginBottom: '1rem'
                  }}>
                    <span className={`ticket-badge ${tier.toLowerCase()}`} style={{ 
                      fontSize: '1rem',
                      padding: '0.5rem 1rem',
                      borderRadius: '8px'
                    }}>
                      {tier.charAt(0).toUpperCase() + tier.slice(1)}
                    </span>
                    <div style={{ 
                      fontSize: '1.5rem', 
                      fontWeight: 'bold',
                      color: '#2c3e50'
                    }}>
                      ${tierPrices[tier] || '---'}
                    </div>
                  </div>
                  
                  {/* Description */}
                  <p style={{ 
                    fontSize: '0.875rem', 
                    color: '#666',
                    marginBottom: '1rem',
                    lineHeight: '1.4'
                  }}>
                    {tierDescriptions[tier] || 'Standard ticket'}
                  </p>
                  
                  {/* Availability Status */}
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      marginBottom: '0.5rem'
                    }}>
                      <span style={{ 
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        color: availStatus.color
                      }}>
                        {availStatus.status}
                      </span>
                      <span style={{ fontSize: '0.875rem', color: '#999' }}>
                        {stats.capacity - stats.available} / {stats.capacity} sold
                      </span>
                    </div>
                    
                    {/* Progress Bar */}
                    <div style={{ 
                      background: '#ecf0f1',
                      borderRadius: '10px',
                      height: '8px',
                      overflow: 'hidden'
                    }}>
                      <div style={{ 
                        background: stats.available === 0 ? '#e74c3c' : 
                                   soldPercentage > 80 ? '#f39c12' : '#3498db',
                        height: '100%',
                        width: `${soldPercentage}%`,
                        transition: 'width 0.3s ease',
                        borderRadius: '10px'
                      }} />
                    </div>
                  </div>
                  
                  {/* Book Button */}
                  <button style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    border: 'none',
                    background: stats.available === 0 ? '#95a5a6' : '#3498db',
                    color: 'white',
                    fontWeight: '600',
                    cursor: stats.available === 0 ? 'not-allowed' : 'pointer',
                    transition: 'background 0.2s'
                  }}
                  disabled={stats.available === 0}
                  onMouseEnter={e => {
                    if (stats.available > 0) {
                      e.target.style.background = '#2980b9';
                    }
                  }}
                  onMouseLeave={e => {
                    if (stats.available > 0) {
                      e.target.style.background = '#3498db';
                    }
                  }}>
                    {stats.available === 0 ? 'Sold Out' : 'Select Tickets'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Fallback if no availability data */}
      {!availability && (
        <div style={{ 
          background: 'white',
          borderRadius: '12px',
          padding: '2rem',
          textAlign: 'center',
          color: '#666'
        }}>
          <p>Ticket availability information is currently unavailable.</p>
          <p>Please contact the event organizer for ticket information.</p>
        </div>
      )}
    </div>
  );
}

export default EventDetails;